import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Globe, Loader2, Check, Camera, ShoppingBag } from "lucide-react";
import { api } from "../lib/api";
import { CameraScanner, extractCode } from "../components/ScanQR";

// Mobile-first shopper view opened from a tokened link — no admin login required.
export default function ShopperRun() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [cam, setCam] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/run/${token}`); setData(data); }
    catch { setErr("This shopping run link is invalid or has expired."); }
  }, [token]);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  const mark = async (payload) => {
    try { const { data: r } = await api.post(`/run/${token}/mark`, payload); toast.success(`✓ ${r.name} bought`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not mark item"); }
  };
  const onScan = (text) => { setCam(false); mark({ code: extractCode(text) }); };

  if (err) return <Centered><p className="text-[#B23B3B] font-medium text-center">{err}</p></Centered>;
  if (!data) return <Centered><Loader2 className="animate-spin text-[#306D29]" size={28} /></Centered>;

  const pct = data.total_items ? Math.round((data.purchased_items / data.total_items) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#FBF5DD] pb-28" data-testid="shopper-run">
      <header className="sticky top-0 z-30 bg-[#0D530E] text-[#FBF5DD] px-5 py-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-8 h-8 rounded-full bg-[#306D29] flex items-center justify-center"><Globe size={16} /></span>
          <span className="font-display text-xl">AfriGate · {data.run.run_number}</span>
        </div>
        <p className="text-sm text-[#FBF5DD]/80">{data.run.assignee ? `Shopper: ${data.run.assignee}` : "Unassigned"}</p>
        <div className="h-2 bg-white/15 rounded-full mt-3 overflow-hidden"><div className="h-full bg-[#E7E1B1]" style={{ width: `${pct}%`, transition: "width 0.3s ease" }} /></div>
        <p className="text-xs mt-1 text-[#E7E1B1]" data-testid="shop-progress">{data.purchased_items}/{data.total_items} items bought ({pct}%)</p>
      </header>

      <div className="p-4 space-y-5 max-w-lg mx-auto">
        {data.total_items === 0 && <p className="text-[#5C5C5C] text-center py-10">Nothing to buy on this run right now 🎉</p>}
        {data.groups.map((g) => (
          <div key={g.store} className="bg-white border border-black/5 rounded-2xl overflow-hidden" data-testid={`shop-store-${g.store}`}>
            <div className="flex items-center justify-between px-4 py-3 bg-[#0D530E] text-[#FBF5DD]">
              <span className="font-display text-lg flex items-center gap-2"><ShoppingBag size={16} /> {g.store}</span>
              <span className="text-sm">{g.purchased_count}/{g.total_count}</span>
            </div>
            <div className="divide-y divide-black/5">
              {g.items.map((it) => (
                <div key={`${it.order_number}~${it.product_id}`} className={`flex items-center gap-3 p-3 ${it.purchased ? "bg-[#2D7A5D]/5" : ""}`} data-testid={`shop-item-${it.order_number}`}>
                  <img src={it.image} alt="" className="w-12 h-12 rounded-lg object-cover bg-[#E7E1B1] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0D530E]">{it.name} <span className="text-[#5C5C5C]">×{it.qty}</span></p>
                    <p className="text-xs text-[#5C5C5C]">{it.order_number} · £{it.expected_cost_gbp}
                      {it.purchase_url && <a href={it.purchase_url} target="_blank" rel="noreferrer" className="ml-2 text-[#306D29] underline">buy</a>}</p>
                  </div>
                  {it.purchased
                    ? <span className="text-xs font-semibold text-[#2D7A5D] flex items-center gap-1 shrink-0"><Check size={14} /> Bought</span>
                    : <button onClick={() => mark({ order_number: it.order_number, product_id: it.product_id })} data-testid={`shop-mark-${it.order_number}`} className="shrink-0 text-sm font-semibold text-white bg-[#306D29] rounded-full px-4 py-2">Found</button>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <button onClick={() => setCam(true)} data-testid="shop-scan-btn"
        className="fixed bottom-5 left-1/2 -translate-x-1/2 bg-[#306D29] text-white font-semibold px-6 py-3.5 rounded-full shadow-lg flex items-center gap-2 z-40">
        <Camera size={18} /> Scan item
      </button>
      {cam && <CameraScanner onDetect={onScan} onClose={() => setCam(false)} />}
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen bg-[#FBF5DD] flex items-center justify-center px-5">{children}</div>;
}
